import { apiRequest } from '../api';
import { getSupabase } from '../supabase/client';
import { t } from '../language';
import { decodeContentKey, type ContentKey } from '../../shared/contentCrypto';

/**
 * Kullanıcının içerik anahtarı (veri koruma programı §5.3). Sunucudan
 * (`GET /api/v1/keys/content`) alınır ve YALNIZCA bellekte tutulur: diske, ayar
 * dosyasına, localStorage'a ya da IndexedDB'ye yazılmaz. Oturum kapanınca (ya da
 * başka kullanıcı girince) unutulur.
 *
 * `writeEncrypted`: yönetim konsolundaki "İçerik şifreleme" açık mı. Birkaç dakikada
 * bir yeniden sorulur (açıp kapatmak istemcilere kısa sürede yansısın). Sorulamazsa
 * son bilinen değer kullanılır; hiç bilinmiyorsa (sunucuya ulaşılamıyor) yazıcılar düz
 * yazar — kayıt kaybolmasın; işaretsiz toplantıyı sunucudaki dönüştürme işi birkaç
 * dakika içinde şifreler (web/lib/jobs/encrypt-backfill.ts).
 *
 * `level`: yazarken neyin şifreleneceği — 1 içerik, 2 + başlık ve katılımcı listeleri,
 * 3 + kişiler, şirketler ve konuşmacı eşlemesi (web/lib/settings/app-settings.ts >
 * EncryptionLevel; alan → seviye: shared/contentCrypto.ts > FIELD_LEVEL). Alanı
 * göndermeyen sunucu 1 sayılır.
 *
 * Masaüstü ana sürecinde ve web uygulamasında aynı modül çalışır.
 */

export type EncryptionLevel = 0 | 1 | 2 | 3;

export interface ContentKeyInfo {
  key: ContentKey | null;
  writeEncrypted: boolean;
  level: EncryptionLevel;
}

const REFRESH_MS = 5 * 60_000;

let cached: { userId: string; at: number; info: Promise<ContentKeyInfo> } | null = null;
let lastKnown: { userId: string; info: ContentKeyInfo } | null = null;
let listening = false;

function listenForSignOut(): void {
  if (listening) return;
  listening = true;
  getSupabase().auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') forgetContentKey();
  });
}

function fetchInfo(userId: string): Promise<ContentKeyInfo> {
  const info = apiRequest<{ key: string | null; writeEncrypted?: boolean; level?: number }>('GET', '/api/v1/keys/content').then((res) => {
    const writeEncrypted = res.writeEncrypted === true;
    const level: EncryptionLevel = !writeEncrypted ? 0 : res.level === 3 ? 3 : res.level === 2 ? 2 : 1;
    const value: ContentKeyInfo = { key: res.key ? decodeContentKey(res.key) : null, writeEncrypted, level };
    lastKnown = { userId, info: value };
    return value;
  });
  const entry = { userId, at: Date.now(), info };
  cached = entry;
  info.catch(() => {
    if (cached === entry) cached = null;
  });
  return info;
}

export async function getContentKeyInfo(userId: string): Promise<ContentKeyInfo> {
  listenForSignOut();
  if (cached?.userId === userId && Date.now() - cached.at < REFRESH_MS) return cached.info;
  try {
    return await fetchInfo(userId);
  } catch (err) {
    if (lastKnown?.userId === userId) return lastKnown.info;
    throw err;
  }
}

/** Okuyucular için: şifreli veri varsa anahtar da vardır. */
export async function getContentKey(userId: string): Promise<ContentKey> {
  const { key } = await getContentKeyInfo(userId);
  if (!key) throw new Error(t('İçerik anahtarı bulunamadı.', 'Content key not found.'));
  return key;
}

export function forgetContentKey(): void {
  cached = null;
  lastKnown = null;
}
